/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IResourceDescription } from "@adapptio/lib-core";
import {
	createEventEmitter,
	emitEvent,
	isNonEmptyArray,
	offEvent,
	onEvent,
	removeAllEventListeners,
	TGetSimpleEventEmitterDataType,
	TSimpleEventListener
} from "@adapptio/lib-shared";
import { IEditor } from "../../../IEditor";
import { ResourceService } from "../../../resources/ResourceService";
import { IEditorResource_Base, IEditorResource_View } from "../../../resources/ResourceTypes";
import { RuntimeService } from "../../../runtime/RuntimeService";
import { LayoutState, LayoutState_Panel, LayoutState_Tabs } from "../../../ui/components/Layout/LayoutState";
import { IView, IViewEditable, IViewInfo } from "../../../view/IView";
import { ViewEditorLayoutConfig } from "./Layout";
import { VIEW_MODEL_MODE, ViewModel } from "../Common/ViewModel";
import { ViewService } from "../../../view/ViewService";
import { ABILITY_ACTIONS, AbilityService } from "../../../ability/AbilityService";
import { isSelectionItemComponent, Viewport } from "@adapptio/lib-components";
import {
	isSelectionItemDataSource,
	isSelectionItemOutlet,
	isSelectionItemParameterAny,
	VIEW_SELECTION_ITEM_TYPE
} from "../Common/ViewEditSelection";
import {
	IEventDefinitionMap,
	ISchemaComponentListModel,
	ISchemaComponentModel,
	ISchemaDataSourceModel,
	ISchemaFlowNodeListModel,
	ISchemaFlowNodeTypeDefinitionMap,
	isSchemaComponentListModel,
	isSchemaComponentModel,
	isSchemaDataSourceModel,
	isSchemaFlowNodeListModel,
	isSchemaFlowNodeModel
} from "@adapptio/lib-blueprint";
import { ICopyData, IPasteData, OnCopyData, OnPasteData } from "../../../copyPaste";
import { BP_IDT_TYPE } from "@adapptio/lib-blueprint/src/IDT/ISchemaIDT";

interface IViewEditorViewParams {
	resourceId: string;
}

export enum VIEW_EDITOR_VIEW_MODE {
	DESIGN = "design",
	PREVIEW = "preview",
	DEBUG = "debug"
}

export class ViewEditorView implements IView<IViewEditorViewParams>, IViewEditable {
	/* Services */
	private abilitySvc: AbilityService;
	// private commandSvc: CommandsService;
	private resourceSvc: ResourceService;
	private runtimeSvc: RuntimeService;
	private viewSvc: ViewService;

	/* Resource */
	private resourceDescriptor: IResourceDescription;
	private resource: IEditorResource_View;

	/** View info change event */
	public onViewInfoChanged = createEventEmitter<void>();

	/** Emitted when resource descriptor changed */
	public onResourceInfoChanged = createEventEmitter<void>();

	/** Document model */
	private viewModel: ViewModel;

	/** Layout state */
	private layout: LayoutState;

	/** View editor mode */
	private editorMode: VIEW_EDITOR_VIEW_MODE = VIEW_EDITOR_VIEW_MODE.DESIGN;

	/** Active layout panel */
	private activePanel: string = null;

	/** If resource was modified */
	public isModified: boolean;

	/** If the view is being saved */
	private saveInProgress = false;

	/* Resource event handlers */
	private handleResourceChange: () => void;
	private handleResourceDelete: TSimpleEventListener<
		TGetSimpleEventEmitterDataType<IEditorResource_Base["onDelete"]>
	>;

	/** Emitted when editor mode change */
	public onModeChange = createEventEmitter();

	/* INTERNAL: Emit to zoom flow editor */
	public __onFlowEditorZoomIn = createEventEmitter<void>();
	public __onFlowEditorZoomOut = createEventEmitter<void>();
	public __onFlowEditorZoomReset = createEventEmitter<void>();
	public __onFlowEditorZoomFit = createEventEmitter<void>();

	/** Flag to suppress event flow panel expand during auto-select */
	private suppressEventFlowExpand = false;

	/**
	 * View constructor
	 *
	 * @param dc Dependency Container
	 */
	public constructor(dc: IEditor) {
		this.abilitySvc = dc.get("abilityService");
		// this.commandSvc = dc.get("commandsService");
		this.resourceSvc = dc.get("resourceService");
		this.runtimeSvc = dc.get("runtimeService");
		this.viewSvc = dc.get("viewService");

		this.layout = new LayoutState(ViewEditorLayoutConfig);
	}

	/**
	 * View init
	 *
	 * @param params Params
	 */
	public async init(params: IViewEditorViewParams, viewId: string): Promise<void> {
		this.resourceDescriptor = this.resourceSvc.getResourceDescriptorById(params.resourceId);
		this.resource = (await this.resourceSvc.getBlueprintById(params.resourceId)) as IEditorResource_View;

		emitEvent(this.onViewInfoChanged);

		if (!this.resource) {
			throw new Error(`Resource '${params.resourceId}' was not found.`);
		}

		this.viewModel = new ViewModel(this.runtimeSvc, {});
		await this.viewModel.loadDocument(this.resource.content);

		onEvent(this.viewModel.onModelChange, () => {
			this.setIsModified(true, true);
		});

		onEvent(this.viewModel.getSelection().onSelectSingle, () => {
			this.layout.getById<LayoutState_Tabs>("panelRight").selectTab(1);
		});

		onEvent(this.viewModel.getSelection().onSelectSelected, () => {
			this.viewModel.getSelection().deselectAllEventNodes();
			this.layout.getById<LayoutState_Tabs>("panelRight").selectTab(1);
		});

		onEvent(this.viewModel.getSelection().onEventNodesChange, () => {
			if (this.viewModel.getSelection().areSomeEventNodesSelected()) {
				this.layout.getById<LayoutState_Tabs>("panelRight").selectTab(1);
			}
		});

		onEvent(this.viewModel.getSelection().onUnselectAll, () => {
			this.layout.getById<LayoutState_Tabs>("panelRight").selectTab(0);
		});

		onEvent(this.viewModel.getSelection().onEventChange, () => {
			if (this.viewModel.getSelection().isEventSelected() && !this.suppressEventFlowExpand) {
				this.layout.getById<LayoutState_Panel>("eventFlow").show();
				this.layout.getById<LayoutState_Panel>("eventFlow").expand();
			}
		});

		// Auto-select matching or first event when selection changes while event flow is open
		onEvent(this.viewModel.getSelection().onChange, () => {
			const selection = this.viewModel.getSelection();
			const eventFlowPanel = this.layout.getById<LayoutState_Panel>("eventFlow");

			// Only act if event flow panel is visible and an event was previously selected
			if (eventFlowPanel.isHidden || !selection.isEventSelected()) {
				return;
			}

			const currentEventName = selection.getEventName();
			const selectedItems = selection.getItems();

			if (selectedItems.length !== 1) {
				return;
			}

			const selectedItem = selectedItems[0];
			const selectedModel = this.viewModel.getDesignContext().getNodeById(selectedItem.nodeId);

			if (!selectedModel) {
				return;
			}

			let eventNames: string[] = [];

			if (selectedItem.type === VIEW_SELECTION_ITEM_TYPE.COMPONENT) {
				const cmpModel = selectedModel as ISchemaComponentModel;
				const eventDefs = cmpModel.componentHandler?.events as IEventDefinitionMap;
				if (eventDefs) {
					eventNames = Object.keys(eventDefs);
				}
			} else if (selectedItem.type === VIEW_SELECTION_ITEM_TYPE.DATASOURCE) {
				const dsModel = selectedModel as ISchemaDataSourceModel;
				const eventDefs = dsModel.dsHandler?.events as IEventDefinitionMap;
				if (eventDefs) {
					eventNames = Object.keys(eventDefs);
				}
			}

			if (eventNames.length === 0) {
				return;
			}

			// If the current event exists on the new target, re-select it; otherwise select first
			const targetEventName = eventNames.includes(currentEventName)
				? currentEventName
				: eventNames[0];

			// Suppress panel expand - keep it in its current state (e.g. minimized)
			this.suppressEventFlowExpand = true;
			selection.selectEvent(selectedModel, targetEventName);
			this.suppressEventFlowExpand = false;
		});

		onEvent(
			this.resource.onChange,
			(this.handleResourceChange = () => {
				this.resource = this.resourceSvc.getResourceById(params.resourceId) as IEditorResource_View;
				this.resourceDescriptor = this.resource.descriptor;
				this.emitViewInfoChanged();
				emitEvent(this.onResourceInfoChanged);
			})
		);

		onEvent(
			this.resource.onDelete,
			(this.handleResourceDelete = ({ deletedByLocalAction }) => {
				if (deletedByLocalAction || !this.isModified) {
					this.isModified = false;
					this.viewSvc.close(viewId);
				}
			})
		);
	}

	public closeEventFlow() {
		this.layout.getById<LayoutState_Panel>("eventFlow").hide();
	}

	public deselectAllEventNode() {
		this.viewModel.getSelection().deselectAllEventNodes();
	}

	private emitViewInfoChanged(): void {
		emitEvent(this.onViewInfoChanged);
	}

	/**
	 * Returns view model
	 */
	public getViewModel(): ViewModel {
		return this.viewModel;
	}

	/**
	 * Returns layout state
	 */
	public getLayout(): LayoutState {
		return this.layout;
	}

	/**
	 * Sets active layout panel
	 * @param id Panel ID
	 */
	public setActiveLayoutPanel(id: string): void {
		this.activePanel = id;
	}

	/**
	 * Returns active layout panel
	 */
	public getActiveLayoutPanel(): string {
		return this.activePanel;
	}

	/**
	 * Returns if active layout panel is one of provided list
	 *
	 * @param panelIdList Panel ID list
	 */
	public isLayoutPanelActive(panelIdList: string[]): boolean {
		return panelIdList.includes(this.activePanel);
	}

	/**
	 * Returns resource service
	 */
	public getResourceService(): ResourceService {
		return this.resourceSvc;
	}

	/**
	 * Can save
	 */
	public canSave(): boolean {
		return (
			this.isModified &&
			!this.isBeingSaved() &&
			this.abilitySvc.can(ABILITY_ACTIONS.WRITE, this.resource)
		);
	}

	/**
	 * Returns resource descriptor
	 */
	public getResourceDescriptor(): IResourceDescription {
		return this.resourceDescriptor;
	}

	public getResource(): IEditorResource_View {
		return this.resource;
	}

	/**
	 * Returns a list of parent resource descriptors
	 */
	public getResourceParentList(): IResourceDescription[] {
		return this.resourceSvc.getResourceParentList(this.resource.id);
	}

	/**
	 * Saves changes
	 * Returns false if save is already in progress or no changes were made.
	 */
	public async save(): Promise<boolean> {
		if (!this.resource) {
			throw new Error("Resource was not loaded.");
		}

		if (!this.isModified || this.saveInProgress) {
			return false;
		}

		const content = this.viewModel.exportDocumentAsYAML();

		this.saveInProgress = true;
		emitEvent(this.onResourceInfoChanged);

		this.setIsModified(false, false);

		try {
			await this.resourceSvc.saveBlueprint(
				this.resource.id,
				content,
				this.resource.metaData.revision,
				"Editor Save"
			);
			// this.viewModel.undoRedoManager.clear()
			this.setIsModified(this.isModified, true);
		} catch (err) {
			this.setIsModified(true, true);
			throw err;
		} finally {
			this.saveInProgress = false;

			emitEvent(this.onResourceInfoChanged);
		}

		return true;
	}

	/**
	 * Returns if a view is being saved
	 */
	public isBeingSaved(): boolean {
		return this.saveInProgress;
	}

	@OnCopyData()
	public handleCopyData(): ICopyData {
		const viewModel = this.getViewModel();
		const dCtx = viewModel.getDesignContext();

		const selection = this.getViewModel().getSelection();
		const items = selection.getItems();
		const eventName = selection.getEventName();
		const eventNodes = selection.getEventNodes();

		const result: ICopyData = {};

		if (eventName && eventNodes?.length > 0) {
			if (!result.viewEventNodes?.length) {
				for (let i = 0; i < eventNodes.length; i++) {
					const eventNode = dCtx.getNodeById(eventNodes[i].nodeId);

					if (isSchemaFlowNodeModel(eventNode)) {
						const clonedNode = eventNode.schema.clone(
							dCtx,
							eventNode,
							eventNode.parent
						);
						clonedNode.outputs = clonedNode.outputs.schema.createDefault(
							dCtx,
							clonedNode
						);
						result.viewEventNodes = result.viewEventNodes || [];
						result.viewEventNodes.push(clonedNode);
					}
				}
			}
		} else {
			for (let i = 0; i < items.length; i++) {
				const selectionItem = items[i];
	
				if (isSelectionItemComponent(selectionItem)) {
					result.components = result.components || [];
					result.components.push(selectionItem.cmpInstance.modelNode);
	
					continue;
				}
	
				if (isSelectionItemDataSource(selectionItem)) {				
					const model = dCtx.getNodeById(selectionItem.nodeId);
	
					if (isSchemaDataSourceModel(model)) {
						result.dataSources = result.dataSources || [];
						result.dataSources.push(model);
					}
	
					continue;
				}
	
				if (isSelectionItemParameterAny(selectionItem)) {
					result.parameters = result.parameters || [];
					result.parameters.push(selectionItem.mapEntry);
					continue;
				}
	
				if (isSelectionItemOutlet(selectionItem)) {
					result.outlets = result.outlets || [];
					result.outlets.push(selectionItem.mapEntry);
					continue;
				}
			}
		}

		return result;
	}

	@OnPasteData()
	public handlePasteData(data: IPasteData): void {
		const viewModel = this.getViewModel();
		const dCtx = viewModel.getDesignContext();
		const bpSchemaModel = viewModel.getModel();

		const viewProps = bpSchemaModel?.props.spec.value.props;

		const componentListModel = viewProps?.content.value as unknown as ISchemaComponentListModel;
		const dataSourceListModel = viewProps?.dataSources.value;
		const paramsMapModel = viewProps?.params.props.props.props;
		const outletsMapModel = viewProps?.outlets.value.constant;

		if (isNonEmptyArray(data.components)) {
			const selection = viewModel.getSelection();
			const selectionItems = selection.getItems();
			const singleSelectionItem = selectionItems.length === 1 ? selectionItems[0] : null;
			let targetContainerModel: ISchemaComponentListModel;
			let index = 0;

			if (isSelectionItemComponent(singleSelectionItem)) {
				const modelNode = singleSelectionItem.cmpInstance.modelNode;
				const modelNodeContent = modelNode.props.props.content;

				if (isSchemaComponentListModel(modelNodeContent)) {
					targetContainerModel = modelNodeContent;
					index = targetContainerModel.items.length;
				} else {
					targetContainerModel = modelNode.parentComponentList;
					index =
						targetContainerModel.items.findIndex((item) => item.nodeId === modelNode.nodeId) + 1;
				}

				targetContainerModel = isSchemaComponentListModel(modelNodeContent)
					? modelNodeContent
					: modelNode.parentComponentList;
			}

			if (!targetContainerModel) {
				targetContainerModel = componentListModel;
				index = targetContainerModel.items.length;
			}

			for (let i = 0; i < data.components.length; i++) {
				const modelIdt = data.components[i];

				const itemModel = targetContainerModel.schema.componentSchema.parse(dCtx, modelIdt, null, {
					componentListModel: targetContainerModel as unknown as ISchemaComponentListModel<
						Record<string, never>
					>
				});

				const newId = dCtx.getUniqueIdentifier(itemModel.id.value, true);
				itemModel.id.schema.setValue(itemModel.id, newId, true);

				targetContainerModel.schema.addItemModel(
					targetContainerModel as unknown as ISchemaComponentListModel<Record<string, never>>,
					index,
					itemModel,
					true
				);
			}
		}

		if (isNonEmptyArray(data.dataSources)) {
			const targetContainerModel = dataSourceListModel;

			for (let i = 0; i < data.dataSources.length; i++) {
				const modelIdt = data.dataSources[i];

				const itemModel = targetContainerModel.schema.dataSourceSchema.parse(dCtx, modelIdt, null, {
					dataSourceListModel: targetContainerModel
				});

				const newId = dCtx.getUniqueIdentifier(itemModel.id.value, true);
				itemModel.id.schema.setValue(itemModel.id, newId, true);

				targetContainerModel.schema.addItemModel(targetContainerModel, 0, itemModel, true);
			}
		}

		if (isNonEmptyArray(data.parameters)) {
			for (let i = 0; i < data.parameters.length; i++) {
				const mapEntryIdt = data.parameters[i];

				const valueModel = paramsMapModel.schema.opts.value.parse(dCtx, mapEntryIdt.value, null);

				const keyValue =
					mapEntryIdt.key.type === BP_IDT_TYPE.SCALAR ? mapEntryIdt.key.value : "parameter";

				paramsMapModel.schema.addElementModel(
					paramsMapModel,
					paramsMapModel.schema.getUniqueKey(paramsMapModel, String(keyValue), true),
					valueModel,
					true
				);
			}
		}

		if (isNonEmptyArray(data.outlets)) {
			for (let i = 0; i < data.outlets.length; i++) {
				const mapEntryIdt = data.outlets[i];

				const valueModel = outletsMapModel.schema.opts.value.parse(dCtx, mapEntryIdt.value, null);

				const keyValue =
					mapEntryIdt.key.type === BP_IDT_TYPE.SCALAR ? mapEntryIdt.key.value : "outlet";

				outletsMapModel.schema.addElementModel(
					outletsMapModel,
					outletsMapModel.schema.getUniqueKey(outletsMapModel, String(keyValue), true),
					valueModel,
					true
				);
			}
		}

		if (isNonEmptyArray(data.viewEventNodes)) {
			const selection = viewModel.getSelection();
			const selectionItems = selection.getItems();
			const selectionEventName = selection.getEventName();

			const addNodes = (eventNodesListModel: ISchemaFlowNodeListModel<ISchemaFlowNodeTypeDefinitionMap>) => {
				if (isSchemaFlowNodeListModel(eventNodesListModel)) {
					for (let i = 0; i < data.viewEventNodes.length; i++) {
						const eventNodeIdt = data.viewEventNodes[i];
						const eventNodeModel = eventNodesListModel.schema.flowNodeSchema.parse(
							dCtx,
							eventNodeIdt
						);

						eventNodeModel.id = `${eventNodeModel.id}${Math.random() * 1000}`;
						let idIndex = 0;

						while (eventNodesListModel.items.some((item) => item.id === eventNodeModel.id)) {
							idIndex = idIndex + 1;
							eventNodeModel.id = eventNodeModel.id + idIndex;
						}

						while (
							eventNodesListModel.items.some(
								(item) =>
									item.position.props.x.value ===
										eventNodeModel.position.props.x.value &&
									item.position.props.y.value === eventNodeModel.position.props.y.value
							)
						) {
							eventNodeModel.position.props.x.schema.setValue(
								eventNodeModel.position.props.x,
								eventNodeModel.position.props.x.value + 20
							);
							eventNodeModel.position.props.y.schema.setValue(
								eventNodeModel.position.props.y,
								eventNodeModel.position.props.y.value + 20
							);
						}

						eventNodesListModel.schema.addItemModel(
							eventNodesListModel,
							eventNodeModel,
							true
						);
					}
				}
			};

			if (selectionEventName && selectionItems.length === 1) {
				const selectionItem = selectionItems[0];

				// Parse into component event
				if (isSelectionItemComponent(selectionItem)) {
					const model = dCtx.getNodeById(selectionItem.nodeId);

					if (isSchemaComponentModel(model)) {
						addNodes(selectionItem.cmpInstance.modelNode.events.props[selectionEventName]?.props?.nodes
							?.template);
					}
				}
				
				// Paste into datasource event
				if (isSelectionItemDataSource(selectionItem)) {
					const model = dCtx.getNodeById(selectionItem.nodeId);

					if (isSchemaDataSourceModel(model)) {
						addNodes(model.events.props[selectionEventName].props?.nodes?.template);
					}
				}
			} else if (selectionEventName) {
				// Paste into view event itself
				addNodes(this.viewModel.getModel().props.spec.value?.props.events.value.props[selectionEventName]?.props?.nodes?.template);
			}
		}
	}

	/**
	 * Disposes view
	 */
	public dispose(): void {
		if (this.viewModel) {
			this.viewModel.dispose();
		}

		removeAllEventListeners(this.onViewInfoChanged);
		removeAllEventListeners(this.onResourceInfoChanged);

		removeAllEventListeners(this.__onFlowEditorZoomIn);
		removeAllEventListeners(this.__onFlowEditorZoomOut);
		removeAllEventListeners(this.__onFlowEditorZoomReset);
		removeAllEventListeners(this.__onFlowEditorZoomFit);

		if (this.resource) {
			offEvent(this.resource.onChange, this.handleResourceChange);
			offEvent(this.resource.onDelete, this.handleResourceDelete);
		}

		this.layout.dispose();

		this.viewModel = null;
	}

	private setIsModified(isModified: boolean, notify: boolean) {
		this.isModified = isModified;

		if (notify) {
			emitEvent(this.onViewInfoChanged);
		}
	}

	/**
	 * Returns if the view has been modified
	 */
	public getIsModified(): boolean {
		return this.isModified;
	}

	/**
	 * Returns if a current view is active
	 */
	public isViewActive(): boolean {
		return this.viewSvc.getCurrentView() === this;
	}

	/**
	 * Returns current editor mode
	 */
	public getMode(): VIEW_EDITOR_VIEW_MODE {
		return this.editorMode;
	}

	/**
	 * Sets the editor mode
	 *
	 * @param editorMode New editor mode
	 */
	public setMode(editorMode: VIEW_EDITOR_VIEW_MODE): void {
		switch (editorMode) {
			case this.editorMode:
				return;
			case VIEW_EDITOR_VIEW_MODE.DESIGN:
				this.viewModel.setMode(VIEW_MODEL_MODE.EDIT);
				break;
			case VIEW_EDITOR_VIEW_MODE.PREVIEW:
				this.viewModel.setMode(VIEW_MODEL_MODE.PREVIEW);
				break;
			default:
				break;
		}

		this.editorMode = editorMode;

		this.emitModeChange();
	}

	/**
	 * Emits event when editor mode changes
	 */
	private emitModeChange(): void {
		window?.dispatchEvent(new CustomEvent(Viewport.UPDATE_MODE_EVENT_TYPE));
		emitEvent(this.onModeChange);
	}

	/**
	 * Returns view info
	 */
	public getViewInfo(): IViewInfo {
		return {
			label: this.resourceDescriptor?.name || "...",
			icon: "mdi/view-dashboard",
			canClose: true,
			canSave: this.canSave(),
			isModified: this.isModified,
			renderWhenNotActive: true,
			tabStyleModifier: {
				"resource-type-view": true
			}
		};
	}

	public undo() {
		this.viewModel.undoRedoManager.undo();
		this.setIsModified(true, true);
	}

	public redo() {
		this.viewModel.undoRedoManager.redo();
		this.setIsModified(true, true);
	}

	public canUndo() {
		return this.viewModel.undoRedoManager.canUndo();
	}

	public canRedo() {
		return this.viewModel.undoRedoManager.canRedo();
	}
}
